import { useEffect, useMemo, useRef, useState } from 'react';
import { subscribe, useWebSocket } from '../../hooks/useWebSocket';
import {
  MOCK_SIGNAL_FLOW,
  MOCK_SLOTS,
  type SignalFlowItem,
  type SignalStage,
  type SlotName,
  type SlotStatus,
  type SlotTriggerRow,
} from './tradingMock';
import './trading.css';

// 권위: data/reports/UNIMPLEMENTED_INVENTORY.md §4.3 — Trading Slot Status + Signal Pipeline.
//   - SIGNAL.RECEIVED / DROPPED / QUEUED / DEFERRED / PROMOTED_TO_FR6 / STALE_EXPIRED 흐름
//   - TRADING.SLOT.STARTED / SUCCEEDED / FAILED / SKIPPED_HOLIDAY 슬롯 상태
//
// 실 ws 토픽 `/topic/system-events` 를 구독하여 TRADING.SIGNAL.* / TRADING.SLOT.* 만 필터.
// `dashboard/api` 의 trading 전용 ws topic 은 부재 — system-events 채널을 1차 source 로 사용.

const STREAM_MAX = 80;

const SLOT_LABELS: Record<SlotName, string> = {
  pre_open: '장 시작 전 (08:50)',
  closing_evaluation: '마감 평가 (15:15)',
  post_market_batch: '장후 배치 (16:30)',
};

export default function TradingLiveMonitorPage(): JSX.Element {
  const { status } = useWebSocket();
  const disconnected = status !== 'connected';

  const [signalFlow, setSignalFlow] = useState<SignalFlowItem[]>(MOCK_SIGNAL_FLOW);
  const [slots, setSlots] = useState<SlotTriggerRow[]>(MOCK_SLOTS);

  // 실 ws 수신 시점에 mock 데이터 위에 prepend.
  const seenIds = useRef<Set<string>>(new Set(MOCK_SIGNAL_FLOW.map((s) => s.signalId)));

  useEffect(() => {
    const off = subscribe('/topic/system-events', (msg) => {
      if (!msg.body) return;
      let p: Record<string, unknown>;
      try {
        p = JSON.parse(msg.body);
      } catch {
        return;
      }
      const code = String(p.eventCode ?? p.event_code ?? '').toUpperCase();
      if (code.startsWith('TRADING.SIGNAL.')) {
        const flow = parseSignalEvent(p, code);
        if (flow && !seenIds.current.has(flow.signalId)) {
          seenIds.current.add(flow.signalId);
          setSignalFlow((prev) => {
            const next = [flow, ...prev];
            if (next.length > STREAM_MAX) next.length = STREAM_MAX;
            return next;
          });
        }
      } else if (code.startsWith('TRADING.SLOT.')) {
        const updated = parseSlotEvent(p, code);
        if (updated) {
          setSlots((prev) =>
            prev.map((s) => (s.slot === updated.slot ? { ...s, ...updated } : s)),
          );
        }
      }
    });
    return off;
  }, []);

  const counts = useMemo(() => countByStage(signalFlow), [signalFlow]);

  return (
    <div className="trading-page">
      <header className="trading-page__header">
        <h1 className="trading-page__title">트레이딩 실시간 모니터</h1>
        <span className="trading-page__subtitle">
          실시간 시그널 · TRADING.SIGNAL.* · TRADING.SLOT.*
        </span>
      </header>

      {disconnected && (
        <div className="trading-page__notice" role="status">
          <strong>오프라인</strong>
          실시간 연결이 끊겼습니다. 표시된 데이터는 mock 또는 마지막 수신 상태일 수 있습니다.
        </div>
      )}

      <section className="trading-kpi-row" aria-label="시그널 stage 카운트">
        <div className="trading-kpi">
          <span className="trading-kpi__label">QUEUED</span>
          <span className="trading-kpi__value trading-num--up">{counts.QUEUED}</span>
        </div>
        <div className="trading-kpi">
          <span className="trading-kpi__label">DEFERRED</span>
          <span className="trading-kpi__value">{counts.DEFERRED}</span>
        </div>
        <div className="trading-kpi">
          <span className="trading-kpi__label">DROPPED</span>
          <span className="trading-kpi__value trading-num--down">{counts.DROPPED}</span>
        </div>
        <div className="trading-kpi">
          <span className="trading-kpi__label">PROMOTED</span>
          <span className="trading-kpi__value">{counts.PROMOTED}</span>
        </div>
      </section>

      <div className="trading-live__grid">
        <section className="trading-card" aria-label="시그널 흐름">
          <header className="trading-card__header">
            <h2 className="trading-card__title">시그널 흐름</h2>
            <span className="trading-page__subtitle">{signalFlow.length} / {STREAM_MAX}</span>
          </header>
          <div className="trading-card__body">
            {signalFlow.length === 0 && (
              <div className="trading-table__empty">수신된 시그널이 없습니다.</div>
            )}
            <ul className="trading-signal-flow">
              {signalFlow.map((s) => (
                <li
                  key={s.signalId}
                  className={`trading-signal-flow__item trading-signal-flow__item--${stageTone(s.stage)}`}
                >
                  <span className="trading-signal-flow__time">{formatTime(s.occurredAt)}</span>
                  <span className="trading-signal-flow__symbol">{s.symbol}</span>
                  <span className={`trading-badge trading-badge--${signalKindTone(s.signalKind)}`}>
                    {s.signalKind}
                  </span>
                  <span className={`trading-badge trading-badge--${stageTone(s.stage)}`}>
                    {s.stage}
                  </span>
                  <span className="trading-signal-flow__reason" title={s.reason ?? ''}>
                    {s.reason ?? '–'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="trading-card" aria-label="슬롯 트리거 상태">
          <header className="trading-card__header">
            <h2 className="trading-card__title">슬롯 트리거</h2>
          </header>
          <div className="trading-card__body">
            <ul className="trading-slot-list">
              {slots.map((s) => (
                <li key={s.slot} className="trading-slot-item">
                  <span className="trading-slot-item__slot">{SLOT_LABELS[s.slot]}</span>
                  <span className={`trading-badge trading-badge--${slotTone(s.status)}`}>
                    {s.status}
                  </span>
                  <span className="trading-slot-item__msg" title={s.message ?? ''}>
                    {s.message ?? '–'}
                  </span>
                  <span className="trading-slot-item__time">
                    {s.lastFinishedAt
                      ? formatTime(s.lastFinishedAt)
                      : s.lastStartedAt
                        ? formatTime(s.lastStartedAt)
                        : '–'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}

function stageTone(stage: SignalStage): 'ok' | 'warn' | 'danger' | 'accent' | 'neutral' {
  switch (stage) {
    case 'QUEUED':
      return 'ok';
    case 'DEFERRED':
      return 'warn';
    case 'DROPPED':
    case 'STALE_EXPIRED':
      return 'danger';
    case 'PROMOTED':
      return 'accent';
    default:
      return 'neutral';
  }
}

function slotTone(status: SlotStatus): 'ok' | 'warn' | 'danger' | 'neutral' {
  switch (status) {
    case 'SUCCEEDED':
      return 'ok';
    case 'STARTED':
      return 'neutral';
    case 'FAILED':
      return 'danger';
    case 'SKIPPED_HOLIDAY':
      return 'warn';
    default:
      return 'neutral';
  }
}

function signalKindTone(
  kind: 'BUY' | 'SELL' | 'STOP_LOSS' | 'TAKE_PROFIT',
): 'ok' | 'warn' | 'danger' | 'accent' {
  if (kind === 'BUY') return 'ok';
  if (kind === 'SELL') return 'warn';
  if (kind === 'STOP_LOSS') return 'danger';
  return 'accent';
}

function countByStage(items: SignalFlowItem[]): Record<SignalStage, number> {
  const out: Record<SignalStage, number> = {
    RECEIVED: 0,
    QUEUED: 0,
    DEFERRED: 0,
    DROPPED: 0,
    PROMOTED: 0,
    STALE_EXPIRED: 0,
  };
  for (const it of items) out[it.stage] += 1;
  return out;
}

function parseSignalEvent(
  p: Record<string, unknown>,
  code: string,
): SignalFlowItem | null {
  const refs = (p.entityRefs ?? p.entity_refs ?? {}) as Record<string, unknown>;
  const signalId =
    (p.signalId as string | undefined) ??
    (refs.signal_id as string | undefined) ??
    (p.eventId as string | undefined) ??
    (p.event_id as string | undefined);
  if (!signalId) return null;
  const symbol =
    (refs.symbol as string | undefined) ??
    (p.symbol as string | undefined) ??
    '?';
  const stage: SignalStage =
    code === 'TRADING.SIGNAL.QUEUED'
      ? 'QUEUED'
      : code === 'TRADING.SIGNAL.DEFERRED'
        ? 'DEFERRED'
        : code === 'TRADING.SIGNAL.PROMOTED_TO_FR6'
          ? 'PROMOTED'
          : code === 'TRADING.SIGNAL.STALE_EXPIRED'
            ? 'STALE_EXPIRED'
            : code.startsWith('TRADING.SIGNAL.DROPPED')
              ? 'DROPPED'
              : 'RECEIVED';
  return {
    signalId,
    symbol,
    signalKind:
      ((refs.signal_kind as string | undefined) ?? 'BUY').toUpperCase() as SignalFlowItem['signalKind'],
    stage,
    reason: (p.summary as string | undefined) ?? null,
    occurredAt:
      (p.occurredAt as string | undefined) ??
      (p.occurred_at as string | undefined) ??
      new Date().toISOString(),
  };
}

function parseSlotEvent(
  p: Record<string, unknown>,
  code: string,
): Partial<SlotTriggerRow> & { slot: SlotName } | null {
  const refs = (p.entityRefs ?? p.entity_refs ?? {}) as Record<string, unknown>;
  const slot = (refs.slot ?? p.slot) as SlotName | undefined;
  if (!slot || !['pre_open', 'closing_evaluation', 'post_market_batch'].includes(slot)) {
    return null;
  }
  const status: SlotStatus =
    code === 'TRADING.SLOT.STARTED'
      ? 'STARTED'
      : code === 'TRADING.SLOT.SUCCEEDED'
        ? 'SUCCEEDED'
        : code === 'TRADING.SLOT.FAILED'
          ? 'FAILED'
          : code === 'TRADING.SLOT.SKIPPED_HOLIDAY'
            ? 'SKIPPED_HOLIDAY'
            : 'IDLE';
  const ts =
    (p.occurredAt as string | undefined) ??
    (p.occurred_at as string | undefined) ??
    new Date().toISOString();
  return {
    slot,
    status,
    message: (p.summary as string | undefined) ?? null,
    lastStartedAt: status === 'STARTED' ? ts : null,
    lastFinishedAt: status === 'STARTED' ? null : ts,
  };
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
